import api, { rpc } from '@/utils/api';
import { Endpoint } from '@/utils/api/endpoints';
import getConfig from 'next/config';

export function subscribeNewsletter(email: string) {
  return api.post('bee2-api/newsletter-signup/', { email });
}

export function emailUs(data: any) {
  return api.post('api/v1/email-us/form/', data);
}

export function sendRingSizer(data: any) {
  return api.post('api/v1/ring-sizer/form/', data);
}

export function saveUserInfo(name: string, email: string) {
  return api.post('/accounts/save-user-info/', `email=${email}&name=${name}`, {
    headers: { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' },
  });
}

export function postContactUs(data: any) {
  return rpc(Endpoint.contactUs, data);
}

export function postPaymentOptions(data: any) {
  return rpc(Endpoint.paymentOptions, data);
}

const MARKETING_GROUP = 'C0004';
const RETRY_MS = 200;
const SLOW_RETRY_MS = 2000;
const RETRY_TIMEOUT_MS = 30000;
const PENDING_IDENTIFY_KEY = 'be_klaviyo_pending_identify';
const PENDING_SUBSCRIPTION_KEY = 'be_klaviyo_pending_subscription';
const AUTH_IDENTIFIED_KEY = 'be_klaviyo_auth_identified';
const ONETRUST_EXPECTED_KEY = '__BE_ONETRUST_EXPECTED__';
const ONETRUST_EXPECTATION_EVENT = 'BEOneTrustExpectationReady';

function hasMarketingConsent(): boolean | null {
  const groups = (window as any).OnetrustActiveGroups;
  if (typeof groups === 'undefined') return null;
  // OneTrust can briefly set "" or ",," before groups are populated — treat as unknown
  if (!String(groups).replace(/,/g, '').trim()) return null;
  return String(groups).indexOf(MARKETING_GROUP) !== -1;
}

/**
 * Layout sets window.__BE_ONETRUST_EXPECTED__ once geo/country inputs are ready.
 * Only when it is explicitly false (CMP will not mount) may null consent fall back
 * to legacy always-send — never on a timer while the CMP may still be loading.
 */
function isOneTrustNotExpected(): boolean {
  return (window as any)[ONETRUST_EXPECTED_KEY] === false;
}

/** True when OneTrust conf/stub/groups are already on the page. */
function isOneTrustPresent(): boolean {
  if (typeof (window as any).OnetrustActiveGroups !== 'undefined') return true;
  const ot = (window as any).OneTrust;
  if (ot && (typeof ot.GetDomainData === 'function' || ot.geolocationResponse)) return true;
  if (document.querySelector('script[src*="cookielaw.org"], script[data-domain-script]')) return true;
  return false;
}

/**
 * Explicit C0004, or Layout confirmed no CMP and OneTrust is not on the page.
 * Never treat null consent as allowed solely because a timer fired.
 */
function allowMarketingSend(): boolean {
  const consent = hasMarketingConsent();
  if (consent === true) return true;
  if (consent === false) return false;
  return isOneTrustNotExpected() && !isOneTrustPresent();
}

type PendingSubscription = { source: string; email: string };

let pendingSubscription: PendingSubscription | null = null;

function readStoredSubscription(): PendingSubscription | null {
  try {
    const raw = sessionStorage.getItem(PENDING_SUBSCRIPTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.email || !String(parsed.email).trim()) return null;
    return {
      source: String(parsed.source ?? ''),
      email: String(parsed.email).trim(),
    };
  } catch {
    return null;
  }
}

function writeStoredSubscription(opts: PendingSubscription | null) {
  try {
    if (!opts?.email || !String(opts.email).trim()) {
      sessionStorage.removeItem(PENDING_SUBSCRIPTION_KEY);
      return;
    }
    sessionStorage.setItem(
      PENDING_SUBSCRIPTION_KEY,
      JSON.stringify({
        source: opts.source ?? '',
        email: String(opts.email).trim(),
      })
    );
  } catch {
    // sessionStorage may be unavailable; same-page deferral still works
  }
}

function setPendingSubscription(opts: PendingSubscription | null) {
  pendingSubscription = opts;
  if (typeof window !== 'undefined') writeStoredSubscription(opts);
}

function getPendingSubscription(): PendingSubscription | null {
  if (pendingSubscription?.email) return pendingSubscription;
  if (typeof window === 'undefined') return null;
  const stored = readStoredSubscription();
  if (stored) pendingSubscription = stored;
  return stored;
}

async function sendKlaviyoSubscription(source: string, email: string): Promise<void> {
  const { NEXT_PUBLIC_KLAVIYO_LIST_ID: KLAVIYO_LIST_ID } = getConfig().publicRuntimeConfig;
  const { NEXT_PUBLIC_KLAVIYO_SUBSCRIPTION_URL: KLAVIYO_SUBSCRIPTION_URL } = getConfig().publicRuntimeConfig;
  const { NEXT_PUBLIC_KLAVIYO_COMPANY_ID: KLAVIYO_COMPANY_ID } = getConfig().publicRuntimeConfig;
  const { NEXT_PUBLIC_KLAVIYO_REVISION: KLAVIYO_REVISION } = getConfig().publicRuntimeConfig;
  const companyId = window.location.hostname.startsWith('www') ? 'RdWGUR' : 'TjyviM';
  const listId = window.location.hostname.startsWith('www') ? 'UhUYRb' : 'YrC9Bw';
  const payload = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: source ?? '',
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              subscriptions: {
                email: {
                  marketing: { consent: 'SUBSCRIBED' },
                },
              },
            },
          },
        },
      },
      relationships: {
        list: {
          data: { type: 'list', id: KLAVIYO_LIST_ID || listId },
        },
      },
    },
  };

  try {
    const url = new URL(KLAVIYO_SUBSCRIPTION_URL || 'https://a.klaviyo.com/client/subscriptions');
    url.searchParams.set('company_id', KLAVIYO_COMPANY_ID || companyId);
    const res = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION || '2026-04-15',
        'content-type': 'application/vnd.api+json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error(`Klaviyo HTTP error ${res.status}: ${res.statusText}`);
  } catch (err) {
    console.error(err);
  }
}

/**
 * Subscribe email via Klaviyo client API. Gated on OneTrust marketing consent (C0004)
 * when the CMP is present; if Layout confirms OneTrust will not mount, falls back to
 * legacy always-send. Pending state is kept in memory + sessionStorage so full-page
 * redirects (e.g. ring sizer) do not drop it.
 */
export async function postKlaviyo(source: string, email: string): Promise<void> {
  if (!email || !String(email).trim()) return;
  if (typeof window === 'undefined') return;

  const trimmed = String(email).trim();
  const consent = hasMarketingConsent();
  if (consent === false) {
    setPendingSubscription({ source, email: trimmed });
    bindOneTrustIdentifyRetry();
    return;
  }
  if (!allowMarketingSend()) {
    // Consent still unknown — wait for C0004 or Layout's "OneTrust not expected" signal
    setPendingSubscription({ source, email: trimmed });
    bindOneTrustIdentifyRetry();
    ensureIdentifyRetry();
    return;
  }

  setPendingSubscription(null);
  await sendKlaviyoSubscription(source, trimmed);
}

function flushPendingSubscription(): void {
  const pending = getPendingSubscription();
  if (!pending?.email) return;
  if (!allowMarketingSend()) return;
  setPendingSubscription(null);
  sendKlaviyoSubscription(pending.source, pending.email).catch(() => {});
}

/**
 * Resume a deferred list subscription after navigation / once auth+consent are known.
 */
export function resumePendingKlaviyoSubscription(): void {
  if (typeof window === 'undefined') return;
  bindOneTrustIdentifyRetry();
  flushPendingSubscription();
  // Subscription-only pending with unknown consent still needs Layout/CMP resolution
  if (getPendingSubscription()?.email && hasMarketingConsent() !== false && !allowMarketingSend()) {
    ensureIdentifyRetry();
  }
}

export type KlaviyoIdentifyOpts = {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

export type KlaviyoIdentifyOptions = {
  /** True when identify came from an authenticated loadUser (not a guest form). */
  fromAuth?: boolean;
  /** Reset the Klaviyo browser cookie before identify (e.g. guest → login). */
  resetBefore?: boolean;
};

type StoredPendingIdentify = KlaviyoIdentifyOpts & { fromAuth?: boolean; resetBefore?: boolean };

let retryTimer: number | null = null;
let retryStarted: number | null = null;
let retryIntervalMs: number | null = null;
let pendingIdentify: KlaviyoIdentifyOpts | null = null;
let pendingFromAuth = false;
let pendingResetBefore = false;
let onetrustIdentifyBound = false;
let pageHideFlushBound = false;
let authUntieTimer: number | null = null;
let authUntieStarted: number | null = null;
let authUntieIntervalMs: number | null = null;
let lastIdentifyPromise: Promise<unknown> | null = null;

function readStoredPending(): StoredPendingIdentify | null {
  try {
    const raw = sessionStorage.getItem(PENDING_IDENTIFY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.email || !String(parsed.email).trim()) return null;
    // Auth-derived pending is intentionally not restored from storage.
    if (parsed.fromAuth) return null;
    return {
      email: String(parsed.email).trim(),
      firstName: parsed.firstName ?? null,
      lastName: parsed.lastName ?? null,
      fromAuth: false,
      resetBefore: !!parsed.resetBefore,
    };
  } catch {
    return null;
  }
}

function writeStoredPending(opts: KlaviyoIdentifyOpts | null, fromAuth: boolean, resetBefore: boolean) {
  try {
    if (!opts?.email || !String(opts.email).trim() || fromAuth) {
      sessionStorage.removeItem(PENDING_IDENTIFY_KEY);
      return;
    }
    sessionStorage.setItem(
      PENDING_IDENTIFY_KEY,
      JSON.stringify({
        email: String(opts.email).trim(),
        firstName: opts.firstName ?? null,
        lastName: opts.lastName ?? null,
        fromAuth: false,
        resetBefore: !!resetBefore,
      })
    );
  } catch {
    // sessionStorage may be unavailable; same-page retry still works
  }
}

function setPendingIdentify(opts: KlaviyoIdentifyOpts | null, fromAuth = false, resetBefore = false) {
  pendingIdentify = opts;
  pendingFromAuth = opts ? fromAuth : false;
  pendingResetBefore = opts ? resetBefore : false;
  if (typeof window !== 'undefined') writeStoredPending(opts, pendingFromAuth, pendingResetBefore);
}

function writeAuthIdentified(identified: boolean) {
  try {
    if (identified) sessionStorage.setItem(AUTH_IDENTIFIED_KEY, '1');
    else sessionStorage.removeItem(AUTH_IDENTIFIED_KEY);
  } catch {
    // ignore
  }
}

/** True when loadUser previously identified an authenticated user in this tab. */
export function wasKlaviyoAuthIdentified(): boolean {
  try {
    return sessionStorage.getItem(AUTH_IDENTIFIED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Mark that the current Klaviyo browser profile came from an authenticated loadUser. */
export function markKlaviyoAuthIdentified(): void {
  if (typeof window === 'undefined') return;
  writeAuthIdentified(true);
}

/** Clear the auth-identified marker (e.g. after untying an expired auth profile). */
export function clearKlaviyoAuthIdentified(): void {
  if (typeof window === 'undefined') return;
  writeAuthIdentified(false);
}

/**
 * Drop a deferred identify that belonged to an authenticated session so an expired
 * login is not re-applied to the anonymous browser after reset.
 */
export function clearDeferredAuthPendingIdentify(): void {
  const stored = typeof window !== 'undefined' ? readStoredPending() : null;
  const fromAuth = pendingFromAuth || !!stored?.fromAuth;
  if (!fromAuth) return;
  setPendingIdentify(null);
  clearIdentifyRetry();
}

function isKlaviyoReady(): boolean {
  const { klaviyo } = window as any;
  return !!klaviyo && typeof klaviyo.identify === 'function';
}

function clearIdentifyRetry() {
  if (retryTimer != null) {
    window.clearInterval(retryTimer);
    retryTimer = null;
  }
  retryStarted = null;
  retryIntervalMs = null;
}

/** Returns true only when klaviyo.reset ran successfully (browser profile untied). */
function tryResetKlaviyoBrowser(): boolean {
  try {
    const { klaviyo } = window as any;
    if (klaviyo && typeof klaviyo.reset === 'function') {
      klaviyo.reset();
      return true;
    }
  } catch (err) {
    console.error(err);
  }
  return false;
}

function clearAuthUntieRetry() {
  if (authUntieTimer != null) {
    window.clearInterval(authUntieTimer);
    authUntieTimer = null;
  }
  authUntieStarted = null;
  authUntieIntervalMs = null;
}

function onAuthUntieTick() {
  if (!wasKlaviyoAuthIdentified()) {
    clearAuthUntieRetry();
    return;
  }
  if (tryResetKlaviyoBrowser()) {
    clearKlaviyoAuthIdentified();
    clearAuthUntieRetry();
    return;
  }
  // Fast phase → slow poll so a late GTM/Klaviyo load can still untie after logout.
  if (
    authUntieIntervalMs === RETRY_MS &&
    authUntieStarted != null &&
    Date.now() - authUntieStarted >= RETRY_TIMEOUT_MS
  ) {
    if (authUntieTimer != null) window.clearInterval(authUntieTimer);
    authUntieIntervalMs = SLOW_RETRY_MS;
    authUntieTimer = window.setInterval(onAuthUntieTick, SLOW_RETRY_MS);
  }
}

/**
 * Untie a prior authenticated Klaviyo browser cookie. Retries until reset succeeds
 * (or the auth marker is cleared by a later identify with resetBefore).
 */
export function ensureKlaviyoAuthSessionUntied(): boolean {
  if (typeof window === 'undefined') return true;
  if (!wasKlaviyoAuthIdentified()) return true;
  if (tryResetKlaviyoBrowser()) {
    clearKlaviyoAuthIdentified();
    clearAuthUntieRetry();
    return true;
  }
  if (authUntieTimer != null) return false;
  authUntieStarted = Date.now();
  authUntieIntervalMs = RETRY_MS;
  authUntieTimer = window.setInterval(onAuthUntieTick, RETRY_MS);
  return false;
}

function tryIdentify(opts: { email: string; firstName?: string | null; lastName?: string | null }): boolean {
  if (!isKlaviyoReady()) return false;

  // Guest identify must untie a prior auth cookie even if the caller omitted resetBefore
  // (footer / ring-sizer after logout on the same Layout mount).
  const needsReset = pendingResetBefore || (!pendingFromAuth && wasKlaviyoAuthIdentified());
  if (needsReset) {
    if (!tryResetKlaviyoBrowser()) return false;
    pendingResetBefore = false;
    clearKlaviyoAuthIdentified();
    clearAuthUntieRetry();
    if (pendingIdentify) {
      writeStoredPending(pendingIdentify, pendingFromAuth, false);
    }
  }

  // Modern klaviyo.js identify expects email / first_name / last_name (not $email).
  // Passing $email alone yields Client API 400: "identify failed with status 400".
  const payload: Record<string, string> = { email: opts.email };
  if (opts.firstName) payload.first_name = opts.firstName;
  if (opts.lastName) payload.last_name = opts.lastName;
  try {
    const result = (window as any).klaviyo.identify(payload);
    lastIdentifyPromise =
      result && typeof result.then === 'function' ? Promise.resolve(result).catch(() => {}) : Promise.resolve();
    return true;
  } catch (err) {
    console.error(err);
    lastIdentifyPromise = null;
    return false;
  }
}

export function flushKlaviyoIdentifyForNavigation(timeoutMs = 4000): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  bindPageHideFlush();

  const IDENTIFY_SETTLE_MS = 1000;

  return new Promise(resolve => {
    let settled = false;
    let pollTimer: number | null = null;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (pollTimer != null) window.clearInterval(pollTimer);
      resolve();
    };
    const timer = window.setTimeout(finish, timeoutMs);

    const attempt = () => {
      if (hasMarketingConsent() === false) {
        finish();
        return;
      }
      if (!flushPendingIdentify()) {
        ensureIdentifyRetry();
        return;
      }
      if (pollTimer != null) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
      if (lastIdentifyPromise) {
        const settle = new Promise<void>(r => {
          window.setTimeout(r, IDENTIFY_SETTLE_MS);
        });
        Promise.all([lastIdentifyPromise, settle]).finally(finish);
        return;
      }
      finish();
    };

    attempt();
    pollTimer = window.setInterval(attempt, RETRY_MS);
  });
}

function flushPendingIdentify(): boolean {
  if (!pendingIdentify?.email) {
    if (allowMarketingSend()) flushPendingSubscription();
    // Subscription-only still waiting on unknown consent (do not treat as done)
    if (getPendingSubscription()?.email && hasMarketingConsent() !== false && !allowMarketingSend()) {
      return false;
    }
    return true;
  }
  const consent = hasMarketingConsent();
  if (consent === false) {
    // Keep pendingIdentify so a later consent accept can still identify (guest or auth)
    clearIdentifyRetry();
    return true;
  }
  // Wait while consent is unknown (null) unless Layout confirmed OneTrust will not mount
  if (!allowMarketingSend()) return false;
  // Same-submit postKlaviyo may be deferred too — flush as soon as marketing is allowed,
  // even if window.klaviyo is not ready yet for identify.
  flushPendingSubscription();
  if (
    tryIdentify({
      email: String(pendingIdentify.email).trim(),
      firstName: pendingIdentify.firstName,
      lastName: pendingIdentify.lastName,
    })
  ) {
    if (pendingFromAuth) markKlaviyoAuthIdentified();
    setPendingIdentify(null);
    clearIdentifyRetry();
    return true;
  }
  return false;
}

function onIdentifyRetryTick() {
  if (flushPendingIdentify()) {
    clearIdentifyRetry();
    return;
  }

  // Fast phase times out while waiting on OneTrust/Layout or late Klaviyo.
  if (retryIntervalMs === RETRY_MS && retryStarted != null && Date.now() - retryStarted >= RETRY_TIMEOUT_MS) {
    if (
      (allowMarketingSend() && pendingIdentify?.email && !isKlaviyoReady()) ||
      (hasMarketingConsent() === null && !isOneTrustNotExpected())
    ) {
      // Keep a slow poll: late GTM/Klaviyo, late C0004, or Layout still deciding.
      // Never flip null consent → allowed on a timer (that bypasses the CMP).
      if (retryTimer != null) window.clearInterval(retryTimer);
      retryIntervalMs = SLOW_RETRY_MS;
      retryTimer = window.setInterval(onIdentifyRetryTick, SLOW_RETRY_MS);
      return;
    }
    clearIdentifyRetry();
  }
}

function ensureIdentifyRetry() {
  if (retryTimer != null) return;
  retryStarted = Date.now();
  retryIntervalMs = RETRY_MS;
  retryTimer = window.setInterval(onIdentifyRetryTick, RETRY_MS);
}

function bindOneTrustIdentifyRetry() {
  if (onetrustIdentifyBound || typeof window === 'undefined') return;
  onetrustIdentifyBound = true;
  const replayPending = () => {
    flushPendingSubscription();
    if (!pendingIdentify?.email) {
      // Subscription-only: flushPendingSubscription already ran; restart poll if still blocked
      if (getPendingSubscription()?.email && !allowMarketingSend()) ensureIdentifyRetry();
      return;
    }
    klaviyoIdentify(pendingIdentify, { fromAuth: pendingFromAuth, resetBefore: pendingResetBefore });
  };
  window.addEventListener('OneTrustGroupsUpdated', replayPending);
  // Layout publishes when geo/country inputs decide whether OneTrust will mount
  window.addEventListener(ONETRUST_EXPECTATION_EVENT, replayPending);
}

function bindPageHideFlush() {
  if (pageHideFlushBound || typeof window === 'undefined') return;
  pageHideFlushBound = true;
  // Best-effort identify before full-page navigations (e.g. ring sizer → /ring_sizer_success/)
  window.addEventListener('pagehide', () => {
    flushPendingIdentify();
  });
}

export function klaviyoIdentify(
  { email, firstName, lastName }: KlaviyoIdentifyOpts = {},
  { fromAuth = false, resetBefore = false }: KlaviyoIdentifyOptions = {}
): void {
  if (!email || !String(email).trim()) return;
  if (typeof window === 'undefined') return;

  // Guest/form identify must untie a prior auth cookie even when callers omit resetBefore
  // (footer / ring-sizer / financing). Auth→auth re-identify leaves the marker alone.
  const effectiveResetBefore = resetBefore || (!fromAuth && wasKlaviyoAuthIdentified());
  setPendingIdentify({ email: String(email).trim(), firstName, lastName }, fromAuth, effectiveResetBefore);
  bindOneTrustIdentifyRetry();
  bindPageHideFlush();

  const consent = hasMarketingConsent();
  if (consent === false) {
    // Keep pendingIdentify for a later consent accept via OneTrustGroupsUpdated
    clearIdentifyRetry();
    return;
  }

  if (flushPendingIdentify()) return;
  ensureIdentifyRetry();
}

export function resumePendingKlaviyoIdentify({
  resetBefore: forceResetBefore = false,
}: { resetBefore?: boolean } = {}): boolean {
  if (typeof window === 'undefined') return false;
  const stored = readStoredPending();
  const pending = pendingIdentify?.email ? pendingIdentify : stored;
  if (!pending?.email) return false;
  const fromAuth = pendingIdentify?.email ? pendingFromAuth : !!stored?.fromAuth;
  if (fromAuth) return false;
  const resetBefore = forceResetBefore || (pendingIdentify?.email ? pendingResetBefore : !!stored?.resetBefore);
  klaviyoIdentify(pending, { fromAuth: false, resetBefore });
  return true;
}

/** True when a non-auth identify is deferred in memory (or legacy storage). */
export function hasDeferredGuestKlaviyoIdentify(): boolean {
  if (typeof window === 'undefined') return false;
  const stored = readStoredPending();
  const pending = pendingIdentify?.email ? pendingIdentify : stored;
  if (!pending?.email) return false;
  const fromAuth = pendingIdentify?.email ? pendingFromAuth : !!stored?.fromAuth;
  return !fromAuth;
}

/**
 * Anonymous session with a known user_email: prefer a deferred guest form identify
 * (footer / ring-sizer / financing) over overwriting it with the ambient session email.
 */
export function klaviyoIdentifyAnonymousSession(
  { email, firstName, lastName }: KlaviyoIdentifyOpts,
  { resetBefore = false }: { resetBefore?: boolean } = {}
): void {
  if (hasDeferredGuestKlaviyoIdentify()) {
    resumePendingKlaviyoIdentify({ resetBefore });
    return;
  }
  klaviyoIdentify({ email, firstName, lastName }, { resetBefore });
}

/**
 * Untie the Klaviyo browser profile without dropping a deferred guest identify.
 * Clears the auth-identified marker only when reset actually ran; otherwise keeps
 * retrying until klaviyo is ready so logout/expired-auth cannot leave the cookie tied.
 */
export function resetKlaviyoBrowserSession(): boolean {
  return ensureKlaviyoAuthSessionUntied();
}

/**
 * Clear pending identify state (e.g. on logout).
 * Does not depend on a later loadUser — if reset cannot run yet, keep retrying untie
 * in-page so a same-mount guest form identify cannot merge into the prior auth cookie.
 */
export function clearKlaviyoIdentify(): void {
  setPendingIdentify(null);
  setPendingSubscription(null);
  clearIdentifyRetry();
  if (tryResetKlaviyoBrowser()) {
    clearKlaviyoAuthIdentified();
    clearAuthUntieRetry();
    return;
  }
  // Marker stays until reset succeeds; ensureKlaviyoAuthSessionUntied keeps polling.
  ensureKlaviyoAuthSessionUntied();
}
